<template>
    <details ref="dropdownDetails" class="dropdown" :class="[menuAlignmentClass]">
      <summary ref="trigger" class="list-none [&>*]:pointer-events-none cursor-pointer">
        <slot name="activator">
        </slot>
      </summary>

      <ul class="menu dropdown-content bg-white shadow-lg rounded-lg" ref="dropdownMenu" :aria-hidden="!isActive" :role="ariaRoleMenu" >
        <transition :name="animation">
          <div v-if="isMobileModal" v-show="isActive" class="background" :aria-hidden="!isActive" />
        </transition>

        <slot />
      </ul>
    </details>
</template>
  
<script>
import { ref, computed, watch, onMounted, provide, nextTick } from 'vue';
import DBtn from '@components/DBtn.vue';
import { onClickOutside } from '@vueuse/core';

/**
* @displayName DDropdown
*/
export default {
  name: 'DDropdown',
  status: 'prototype',
  release: '0.1.7',
  components: {
    DBtn,
  },

  props: {
    modelValue: {
      type: [String, Number, Boolean, Object, Array, Symbol, Function],
      default: null,
    },
    text: {
      type: String,
      default: 'Button',
    },
    disabled: Boolean,
    hoverable: Boolean,
    inline: Boolean,

    /**
     * Position Alignment
     */
    position: {
      type: String,
      default: 'right',
      validator: (value) => [null, 'right', 'left'].includes(value),
    },
    mobileModal: {
      type: Boolean,
      default: true,
    },

    /**
     * Role attribute to be passed to list container for better accessibility. Use menu only in situations where your dropdown is related to navigation menus.
     */
    ariaRole: {
      type: String,
      default: 'list',
      validator: (value) => [null, 'menu', 'list'].includes(value),
    },
    animation: {
      type: String,
      default: 'ease-quad',
    },

    menuAlignment: {
      type: String,
      default: 'right',
      validator: (value) => ['left', 'right'].includes(value),
    }
  },

  emits: ['update:modelValue', 'change', 'active-change'],

  setup(props, { emit }) {
    const dropdownDetails = ref(null);
    const trigger = ref(null);
    const dropdownMenu = ref(null);
    const selected = ref(props.modelValue);
    const isActive = ref(false);
    
    // Define _isDropdown for internal use by DropdownItem
    const _isDropdown = true;

    const rootClasses = computed(() => ({
      'is-disabled': props.disabled,
      'is-hoverable': props.hoverable,
      'is-inline': props.inline,
      'is-active': isActive.value || props.inline,
      'is-mobile-modal': isMobileModal.value,
    }));

    // const dropdownMenuClasses = computed(() => [
    //   [`${props.position}-0`],
    //   [`origin-top-${props.position}`],
    // ]);

    const menuAlignmentClass = computed(() => {
      switch(props.menuAlignment) {
        case 'left': return 'dropdown-start';
        case 'right': return 'dropdown-end';
        default: return '';
      }
    })

    const isMobileModal = computed(() => 
      props.mobileModal && !props.inline && !props.hoverable
    );

    const ariaRoleMenu = computed(() => 
      props.ariaRole === 'menu' || props.ariaRole === 'list' ? props.ariaRole : null
    );

    // Watch for changes in props.modelValue and update selected
    watch(() => props.modelValue, (value) => {
      selected.value = value;
    });

    // Watch for changes in isActive and emit active-change event
    watch(isActive, (value) => {
      emit('active-change', value);
    });

    // Toggle dropdown if it's not disabled
    const toggle = () => {
      if (props.disabled || props.hoverable) return;

      if (!isActive.value) {
        // if not active, toggle after clickOutside event
        // this fixes toggling programmatic
        nextTick(() => { isActive.value = !isActive.value; });
      } else {
        isActive.value = !isActive.value;
      }
    };

    // Click listener from DropdownItem
    const selectItem = (value) => {
      if (selected.value !== value) {
        emit('change', value);
        selected.value = value;
      }
      emit('update:modelValue', value);
      isActive.value = false;
    };

    // Set up event listeners
    onMounted(() => {
      onClickOutside(dropdownMenu, (e) => {
        if (props.inline) {
          console.warn('Click outside is not supported for inline dropdowns.');
          return;
        }

        isActive.value = false;

        // Let the dropdown close if the clicked element is not the trigger
        if (e.target === trigger.value) {
          return;
        }

        if (dropdownDetails.value && dropdownDetails.value.hasAttribute('open')) {
            dropdownDetails.value.removeAttribute('open');
        }
      });
    });

    // Provide dropdown context to child components (especially DDropdownItem)
    provide('dropdown', {
      disabled: computed(() => props.disabled),
      selected,
      selectItem,
    });

    return {
      dropdownDetails,
      trigger,
      dropdownMenu,
      selected,
      isActive,
      rootClasses,
      menuAlignmentClass,
      isMobileModal,
      ariaRoleMenu,
      toggle,
      selectItem,
    };
  },
};
</script>